import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { videoPlayed } from '../../app/Search/actions/SearchActions';
import { selectLastPlayingVideoId } from '../../app/Search/selectors/searchSelectors';

// Global registry to track video refs by ID
const videoRefs = new Map<number, React.RefObject<HTMLVideoElement>>();

// A browser that blocks autoplay rejects every play() the same way, and each
// rejection re-renders the caller, whose hover effect calls play() again.
// Capping attempts per hover is what keeps the main thread from spinning; one
// retry still covers transient failures.
const MAX_PLAY_ATTEMPTS_PER_HOVER = 2;

export const useMultiVideoPlayback = (
  videoId: number,
  videoRef: React.RefObject<HTMLVideoElement>
) => {
  const dispatch = useDispatch();
  const lastPlayingVideoId = useSelector(selectLastPlayingVideoId);
  const isPlaying = lastPlayingVideoId === videoId;
  const [playPromiseProcessing, setPlayPromiseProcessing] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [hasGivenUp, setHasGivenUp] = useState(false);
  const playAttempts = useRef(0);
  // Starting play() re-renders the caller, whose hover effect calls play()
  // again before the first promise settles. Without this guard that second
  // call is a concurrent duplicate that spends the retry.
  const playInFlight = useRef(false);
  // reset() bumps this when the hover ends, so a rejection that arrives late
  // can tell its hover is over and must not restart a card the user has left.
  const hoverGeneration = useRef(0);

  // Register this video ref globally
  useEffect(() => {
    videoRefs.set(videoId, videoRef);
    return () => {
      videoRefs.delete(videoId);
    };
  }, [videoId, videoRef]);

  const pause = useCallback(() => {
    if (videoRef.current && !playPromiseProcessing) {
      videoRef.current.pause();
    }
  }, [videoRef, playPromiseProcessing]);

  const play = useCallback(() => {
    if (
      videoRef.current &&
      !hasPlayed &&
      !playInFlight.current &&
      playAttempts.current < MAX_PLAY_ATTEMPTS_PER_HOVER
    ) {
      // Pause the last playing video if it's different from this one
      if (lastPlayingVideoId && lastPlayingVideoId !== videoId) {
        const lastVideoRef = videoRefs.get(lastPlayingVideoId);
        if (lastVideoRef?.current) {
          lastVideoRef.current.pause();
        }
      }

      // Play this video with promise handling
      playAttempts.current += 1;
      playInFlight.current = true;
      const generation = hoverGeneration.current;
      const playPromise = videoRef.current.play();
      setPlayPromiseProcessing(true);
      if (playPromise) {
        playPromise
          .then(() => {
            playInFlight.current = false;
            setPlayPromiseProcessing(false);
            setHasPlayed(true);
            setHasGivenUp(false);
            playAttempts.current = 0;
            dispatch(videoPlayed(videoId));
          })
          .catch((e) => {
            playInFlight.current = false;
            setPlayPromiseProcessing(false);
            // An AbortError is expected when a pause interrupts the play() request.
            const isAbort =
              e instanceof DOMException && e.name === 'AbortError';
            if (isAbort || generation !== hoverGeneration.current) return;
            setHasPlayed(false);
            if (playAttempts.current < MAX_PLAY_ATTEMPTS_PER_HOVER) {
              play();
            } else {
              setHasGivenUp(true);
            }
          });
      }
    }
  }, [videoRef, lastPlayingVideoId, videoId, dispatch, hasPlayed, pause]);

  const reset = useCallback(() => {
    setHasPlayed(false);
    setHasGivenUp(false);
    playAttempts.current = 0;
    hoverGeneration.current += 1;
  }, []);

  return {
    isPlaying,
    hasGivenUp,
    play,
    pause,
    reset,
  };
};
